import { AddCircle, RemoveCircleOutline } from "@mui/icons-material";
import { Box, Button, FormControl, FormHelperText, Link, MenuItem, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useAlert } from "hooks/alerts/useAlert";
import usePickup from "hooks/pickups/usePickup";
import useUpdatePickup from "hooks/pickups/useUpdatePickup";
import useContacts from "hooks/useContacts";
import theme from "lib/theme";
import { useCallback, useEffect, useRef, useState } from "react";
import { PickupContact } from "types/pickupContact";
import * as yup from "yup";
import { AppSelect, InputBase, InputLabel } from "components/common/Input";
import { useMissingRequirement } from "./requirementError";

interface PickupMainContactProps {
  step: number;
  goToNextStep: () => void;
  registerPendingSave?: (saveSelectedContact: () => Promise<boolean>) => void;
}

export const contactValidationSchema = yup.object({
  email: yup.string().email("Invalid Email").required("Required"),
  firstName: yup.string().required("Required"),
  lastName: yup.string().required("Required"),
  phoneNumber: yup.string().required("Required"),
});

const emptyMainContact = (): PickupContact => ({
  email: "",
  firstName: "",
  lastName: "",
  phoneNumber: "",
  pointOfContactType: "main",
});

const ContactDetailsForm = ({
  contact,
  onSubmit,
}: {
  contact: PickupContact;
  onSubmit: (values: PickupContact) => Promise<void>;
}) => {
  const { handleSubmit, getFieldProps, touched, errors, isValid, isSubmitting } = useFormik({
    initialValues: {
      email: contact.email,
      firstName: contact.firstName,
      lastName: contact.lastName,
      phoneNumber: contact.phoneNumber || "",
    },
    enableReinitialize: true,
    validationSchema: contactValidationSchema,
    onSubmit: (values) =>
      onSubmit({ ...values, pointOfContactType: "main", uuid: contact.uuid }).catch(() => undefined),
    validateOnMount: true,
  });

  return (
    <Box mt={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
        }}
      >
        <FormControl variant="standard">
          <InputLabel shrink htmlFor="contact-first-name">
            First name
          </InputLabel>
          <InputBase
            type="text"
            id="contact-first-name"
            {...getFieldProps("firstName")}
            helperText={touched.firstName && errors.firstName}
            error={touched.firstName && !!errors.firstName}
          />
        </FormControl>
        <FormControl variant="standard">
          <InputLabel shrink htmlFor="contact-last-name">
            Last name
          </InputLabel>
          <InputBase
            type="text"
            id="contact-last-name"
            {...getFieldProps("lastName")}
            helperText={touched.lastName && errors.lastName}
            error={touched.lastName && !!errors.lastName}
          />
        </FormControl>
        <FormControl variant="standard">
          <InputLabel shrink htmlFor="contact-phone">
            Phone
          </InputLabel>
          <InputBase
            type="text"
            id="contact-phone"
            {...getFieldProps("phoneNumber")}
            helperText={touched.phoneNumber && errors.phoneNumber}
            error={touched.phoneNumber && !!errors.phoneNumber}
          />
        </FormControl>
        <FormControl variant="standard">
          <InputLabel shrink htmlFor="contact-email">
            Email
          </InputLabel>
          <InputBase
            type="email"
            id="contact-email"
            {...getFieldProps("email")}
            helperText={touched.email && errors.email}
            error={touched.email && !!errors.email}
          />
        </FormControl>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
        <Button onClick={() => handleSubmit()} disabled={isSubmitting || !isValid}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default function PickupMainContact({ step, goToNextStep, registerPendingSave }: PickupMainContactProps) {
  const [showNewContact, setShowNewContact] = useState(false);
  const { contacts, createContact, updateContact } = useContacts();
  const { updatePickup } = useUpdatePickup();
  const { pickup, refetch: refetchPickup } = usePickup();
  const { alertSuccess, alertError } = useAlert();
  const [mainContactEmail, setMainContactEmail] = useState("");
  const [selectedContact, setSelectedContact] = useState<PickupContact | null>(null);
  const initializedFromPickupRef = useRef(false);

  const findContactByEmail = useCallback(
    (email: string): PickupContact | undefined => {
      const organizationContact = contacts?.find((u) => u.email === email);
      if (organizationContact) return organizationContact;

      const pickupContact = pickup?.pickupContacts.find((c) => c.email === email);
      if (pickupContact) return pickupContact;

      return undefined;
    },
    [contacts, pickup?.pickupContacts]
  );

  const saveMainContactToPickup = useCallback(
    (contact: PickupContact) => {
      return updatePickup({ contacts: [contact] })?.then(async () => {
        setMainContactEmail(contact.email);
        setSelectedContact(contact);
        await refetchPickup();
        goToNextStep();
      });
    },
    [updatePickup, refetchPickup, goToNextStep]
  );

  const handleUpdateExistingContact = useCallback(
    async (values: PickupContact) => {
      try {
        const organizationContact = contacts?.find((contact) => contact.email === selectedContact?.email);
        if (organizationContact?.uuid) {
          await updateContact(organizationContact.uuid, values);
        }
        const savedContact: PickupContact = { ...values, uuid: organizationContact?.uuid, pointOfContactType: "main" };
        setSelectedContact(savedContact);
        setMainContactEmail(savedContact.email);
        await saveMainContactToPickup(savedContact);
        alertSuccess("Successfully updated contact");
      } catch {
        alertError("There was an error, please try again.");
        throw new Error("update failed");
      }
    },
    [contacts, selectedContact?.email, updateContact, saveMainContactToPickup, alertSuccess, alertError]
  );

  const handleCreateContact = useCallback(
    async (values: PickupContact) => {
      try {
        const { data: created } = await createContact(values);
        const savedContact: PickupContact = {
          ...values,
          uuid: created.uuid,
          pointOfContactType: "main",
        };
        await saveMainContactToPickup(savedContact);
        setShowNewContact(false);
        alertSuccess("Successfully created a contact");
      } catch (error: unknown) {
        const emailError = (error as { response?: { data?: { errors?: { email?: unknown } } } }).response?.data?.errors
          ?.email;
        alertError(emailError ? "A contact with this email already exists" : "There was an error, please try again.");
        throw new Error("create failed");
      }
    },
    [createContact, saveMainContactToPickup, alertSuccess, alertError]
  );

  const handleChange = (event: { target: { value: string } }) => {
    const contact = findContactByEmail(event.target.value);
    if (!contact) return;
    setShowNewContact(false);
    setSelectedContact(contact);
    setMainContactEmail(contact.email);
  };

  useEffect(() => {
    initializedFromPickupRef.current = false;
  }, [pickup?.uuid]);

  useEffect(() => {
    if (initializedFromPickupRef.current || !pickup) return;

    const currentMainContact = pickup.pickupContacts.find((contact) => contact.pointOfContactType === "main");
    if (!currentMainContact) return;

    initializedFromPickupRef.current = true;
    setMainContactEmail(currentMainContact.email);
    const organizationContact = contacts?.find((contact) => contact.email === currentMainContact.email);
    setSelectedContact(organizationContact || currentMainContact);
  }, [pickup, contacts]);

  const saveSelectedMainContact = useCallback(async () => {
    if (showNewContact || !selectedContact?.email) return true;

    const currentMainContact = pickup?.pickupContacts.find((contact) => contact.pointOfContactType === "main");
    if (currentMainContact?.email === selectedContact.email) return true;

    try {
      await updatePickup({ contacts: [{ ...selectedContact, pointOfContactType: "main" }] });
      return true;
    } catch {
      alertError("There was an error, please try again.");
      return false;
    }
  }, [showNewContact, selectedContact, pickup?.pickupContacts, updatePickup, alertError]);

  registerPendingSave?.(saveSelectedMainContact);

  const formContact = showNewContact ? emptyMainContact() : selectedContact;
  const contactMissing = useMissingRequirement("schedule-pickup-main-contact") && !mainContactEmail;

  return (
    <Box>
      <Typography variant="h6" fontSize="18px" fontWeight="bold">
        {step}. Choose a main point of contact for this pickup
      </Typography>
      <Typography mt={0.5} variant="body2">
        On the day of pickup, this person should be available to communicate with our team.
      </Typography>

      <Box mt={3}>
        <Typography fontWeight={600}>Select one:</Typography>

        <FormControl
          id="schedule-pickup-main-contact"
          error={contactMissing}
          variant="standard"
          sx={{ mt: 0.5, width: "100%" }}
        >
          <AppSelect error={contactMissing} fullWidth onChange={(e) => handleChange(e)} value={mainContactEmail}>
            {contacts?.map((user) => {
              return (
                <MenuItem value={user.email} key={user.uuid}>
                  {(user.firstName || user.lastName) && (
                    <Box sx={{ pr: "6px" }} fontWeight={"800"}>
                      {`${[user.firstName, user.lastName].filter(Boolean).join(" ")}`}
                    </Box>
                  )}
                  {user.phoneNumber && <Box sx={{ pr: "6px" }}>{user.phoneNumber}</Box>}
                  {user.email && <Box>{user.email}</Box>}
                </MenuItem>
              );
            })}
            {pickup?.pickupContacts &&
              pickup.pickupContacts
                .filter(({ email }) => !contacts?.find((u) => u.email === email))
                .map((contact) => (
                  <MenuItem key={contact.email} value={contact.email}>
                    {(contact.firstName || contact.lastName) && (
                      <Box sx={{ pr: "6px" }} fontWeight={"800"}>
                        {`${[contact.firstName, contact.lastName].filter(Boolean).join(" ")}`}
                      </Box>
                    )}
                    {contact.phoneNumber && <Box sx={{ pr: "6px" }}>{contact.phoneNumber}</Box>}
                    {contact.email && <Box>{contact.email}</Box>}
                  </MenuItem>
                ))}
          </AppSelect>
          {contactMissing && <FormHelperText>Required</FormHelperText>}
        </FormControl>
      </Box>

      <Link
        component="button"
        onClick={() => {
          setShowNewContact(!showNewContact);
          setMainContactEmail(showNewContact ? selectedContact?.email ?? "" : "");
        }}
        sx={{
          mt: 2,
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 600,
          textDecoration: "none",
          fontSize: "16px",
          fontFamily: "Catamaran",
          ":hover": {
            textDecoration: "underline",
          },
        }}
      >
        {showNewContact ? (
          <RemoveCircleOutline sx={{ width: "20px", height: "20px" }} />
        ) : (
          <AddCircle sx={{ width: "20px", height: "20px" }} />
        )}
        Add a new contact
      </Link>

      {formContact && (showNewContact || contacts) && (
        <ContactDetailsForm
          key={showNewContact ? "new-contact" : formContact.email}
          contact={formContact}
          onSubmit={showNewContact ? handleCreateContact : handleUpdateExistingContact}
        />
      )}
    </Box>
  );
}
